import React, {PureComponent} from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';
import i18n from '../../../utils/i18n';
import SvgIcon from '@adsk/bim360-matrix-react-svg-icon';
import MatrixTable from '@adsk/bim360-matrix-react-matrix-table';
import iconBlock from '@adsk/bim360-matrix-react-assets/icon-block.svg';
import iconCheck from '@adsk/bim360-matrix-react-assets/icon-check.svg';
import lockIcon from '@adsk/bim360-matrix-react-assets/lock.svg';
import editIcon from '@adsk/bim360-matrix-react-assets/edit-icon.svg';
import starIcon from '@adsk/bim360-matrix-react-assets/icon-star.svg';
import lodashValues from 'lodash/values';
import {v4 as getUUID} from 'uuid';
import {ISSUE_TYPES_ATTRIBUTES, ISSUE_TYPES_PERMITTED_ACTIONS, STATUS_SETS} from '../../../constants/issue-types';
import {isInheritedAttribute} from '../../../utils/issueAttributes-helpers';
import {isStandardType, sortIssueTypeByOrderIndex} from '../../../utils/issueTypes-helpers';
import IssueTypesAdminTitleContainer from './IssueTypesAdminTitleContainer';
import IssueTypesAdminStatus from './IssueTypesAdminStatus';
import IssueTypesAdminAttributesTableCell from './IssueTypesAdminAttributesTableCell';
import {ISSUE_ATTRIBUTE_NAMES} from '../../../constants/issue-attribute-names';

class IssueTypesAdminTable extends PureComponent {
  constructor(props) {
    super(props);

    this.state = {
      tableData: this.prepareTableData(props.data, props.issueTypeIds),
      disableRowOnClick: false
    };

    this._renderStatus = this._renderStatus.bind(this);
    this._renderAttributes = this._renderAttributes.bind(this);
    this._getStatusSet = this._getStatusSet.bind(this);
    this._renderTitle = this._renderTitle.bind(this);
    this.onIssueSubTypeCreate = this.onIssueSubTypeCreate.bind(this);
    this.setRowClassName = this.setRowClassName.bind(this);
    this.handleRowAction = this.handleRowAction.bind(this);
    this.getActionOptions = this.getActionOptions.bind(this);
    this.onRowClick = this.onRowClick.bind(this);

    this.columns = this.getColumnProps();
    this.actionColumn = this.getActionColumnProps();
  }

  componentWillReceiveProps(nextProps) {
    if (this.props.data !== nextProps.data) {
      this.setState({tableData: this.prepareTableData(nextProps.data, nextProps.issueTypeIds)});
    }
  }

  prepareTableData(data = {}, issueTypeIds = []) {
    return Array.prototype.map
      .call(issueTypeIds, issueTypeId => data[issueTypeId])
      .sort(sortIssueTypeByOrderIndex)
      .map(issueType => {
        const childrenIds = issueType.childrenIds;
        const isContainerType = issueType.isContainerType;
        const children = childrenIds
          ? Array.prototype.map.call(childrenIds, subTypeId => data[subTypeId]).sort(sortIssueTypeByOrderIndex)
          : [];
        if (!isContainerType) {
          children.push({id: getUUID(), parentId: issueType.id, isNewSubType: true});
        }
        return {...issueType, children};
      });
  }

  getActionColumnProps() {
    return {
      key: 'action',
      title: '',
      width: 80,
      resizable: false,
      style: {
        paddingRight: '20px'
      }
    };
  }

  getColumnProps() {
    const columns = [
      {
        key: 'title',
        dataKey: 'title',
        title: i18n.t('issueTypes.header.title'),
        width: 400,
        resizable: false,
        cellRenderer: this._renderTitle,
        className: 'IssueTypesAdminTable__title-column'
      },
      {
        key: 'status',
        dataKey: 'isActive',
        title: i18n.t('issueTypes.header.status'),
        width: 200,
        resizable: false,
        cellRenderer: this._renderStatus,
        className: 'IssueTypesAdminTable__status-column'
      },
      {
        key: 'statusSet',
        dataKey: 'statusSet',
        title: i18n.t('issueTypes.header.statusSet'),
        width: 100,
        resizable: true,
        dataGetter: this._getStatusSet,
        className: 'IssueTypesAdminTable__status-set-column'
      },
      {
        key: 'attributes',
        dataKey: 'mappingIds',
        title: i18n.t('issueTypes.header.attributes'),
        width: 250,
        resizable: true,
        cellRenderer: this._renderAttributes,
        className: 'IssueTypesAdminTable__attributes-column'
      }
    ];

    return columns.map(({key, ...rest}) => <MatrixTable.Column key={key} {...rest} />);
  }

  setRowClassName({rowData, rowIndex}) {
    return cx('IssueTypesAdminTable__row', {
      'IssueTypesAdminTable__row--highlighted':
        this.props.highlightLastRow && rowIndex === this.state.tableData.length - 1,
      'IssueTypesAdminTable__row--child': !!rowData.parentId,
      'IssueTypesAdminTable__row--parent': !rowData.parentId,
      'IssueTypesAdminTable__row--container': rowData.isContainerType,
      'IssueTypesAdminTable__row--clickable': !this.state.disableRowOnClick
    });
  }

  handleRowAction({key, rowData}) {
    // eslint-disable-next-line default-case
    switch (key) {
      case 'status':
        this.props.onStatusChange(rowData);
        break;
      case 'edit':
        this.props.openEditModal({rowData});
        break;
    }
  }

  getActionOptions({rowData}) {
    const {
      isActive,
      isNewSubType,
      isContainerType,
      [ISSUE_ATTRIBUTE_NAMES.PERMITTED_ACTIONS]: permittedActions,
      [ISSUE_ATTRIBUTE_NAMES.PERMITTED_ATTRIBUTES]: permittedAttributes
    } = rowData;
    if (isNewSubType || isContainerType) {
      return [];
    }

    const canEdit = permittedActions && permittedActions.includes(ISSUE_TYPES_PERMITTED_ACTIONS.CAN_EDIT);
    const canChangeStatus =
      canEdit && permittedAttributes.includes(ISSUE_TYPES_ATTRIBUTES.STATUS) && this.canChangeStatus(rowData);

    const actions = [
      {
        key: 'edit',
        label: i18n.t('issueTypes.actions.edit'),
        svg: editIcon,
        width: '16px',
        height: '16px'
      }
    ];

    canChangeStatus &&
      actions.push({
        key: 'status',
        label: isActive ? i18n.t('issueTypes.actions.setStatusInactive') : i18n.t('issueTypes.actions.setStatusActive'),
        svg: isActive ? iconBlock : iconCheck,
        width: '16px',
        height: '16px'
      });

    return actions;
  }

  /**
   * Determines if status can be changed on Issue type or sub-type
   * User cannot disable the last issue type or the last active sub-type
   * @param rowData
   * @returns {*}
   */
  canChangeStatus(rowData) {
    // Changing from inactive to active is always allowed
    if (!rowData.isActive) {
      return true;
    }

    if (rowData.parentId) {
      const parent = this.props.data[rowData.parentId];
      return parent.childrenIds.some(entityId => this.props.data[entityId].isActive && entityId !== rowData.id);
    }
    return lodashValues(this.props.data).some(
      entity => entity.isActive && !entity.parentId && !entity.isContainerType && entity.id !== rowData.id
    );
  }

  onIssueSubTypeCreate() {
    this.props.scrollDown({offset: 50, speed: 0.1, scrollToBottom: false});
  }

  onRowClick({rowData}) {
    if (!this.state.disableRowOnClick) {
      this.props.openEditModal({rowData});
    }
  }

  _renderTitle({rowData, container}) {
    const {parentId, id, isNewSubType, isContainerType, permittedActions, permittedAttributes} = rowData;
    const isStandard = isStandardType(rowData);
    const canEdit = permittedActions && permittedActions.includes(ISSUE_TYPES_PERMITTED_ACTIONS.CAN_EDIT);
    const canChangeTitle = isNewSubType || (canEdit && permittedAttributes.includes(ISSUE_TYPES_ATTRIBUTES.TITLE));

    return (
      <div className="IssueTypesAdminTable__title-cell">
        {isContainerType && (
          <SvgIcon width="13px" height="12px" className="IssueTypesAdminTable__star-icon">
            {starIcon}
          </SvgIcon>
        )}
        {
          <IssueTypesAdminTitleContainer
            entityId={isNewSubType ? null : id}
            clientID={isNewSubType ? id : null}
            parentId={parentId}
            container={container}
            isReadOnly={!canChangeTitle || isContainerType}
            onCreate={this.onIssueSubTypeCreate}
            onTitleEditStart={() => this.setState({disableRowOnClick: true})}
            onTitleEditEnd={() => {
              // setTimeout 100 is in order to actually block the onClick (0 is too fast so the onClick doesn't get blocked).
              setTimeout(() => this.setState({disableRowOnClick: false}), 100);
            }}
          />
        }
        {isStandard && (
          <SvgIcon width="16px" height="14px" className="IssueTypesAdminTable__lock">
            {lockIcon}
          </SvgIcon>
        )}
      </div>
    );
  }

  _renderStatus({rowData, cellData: isRowActive}) {
    const {parentId, isNewSubType, isContainerType} = rowData;

    if (isNewSubType) {
      return null;
    }

    const parent = parentId && this.props.data[parentId];
    const isDisabled = parent && !parent.isActive;
    const isActive = parent ? parent.isActive && isRowActive : isRowActive;

    return <IssueTypesAdminStatus isContainerType={isContainerType} isActive={isActive} disabled={isDisabled} />;
  }

  _getStatusSet({rowData}) {
    const {parentId, isContainerType, isNewSubType} = rowData;
    if (isContainerType || isNewSubType) {
      return '-';
    }

    const issueType = parentId ? this.props.data[parentId] : rowData;
    return issueType.statusSet === STATUS_SETS.BASIC
      ? i18n.t('issueTypes.statusSet.basic')
      : i18n.t('issueTypes.statusSet.field');
  }

  _renderAttributes({rowData, cellData: attributes, container}) {
    const {parentId, isContainerType} = rowData;
    if (attributes) {
      const isSubType = !!parentId;
      const issueTypeAttributes = attributes
        .map(attribute => this.props.issueAttributeMappings[attribute])
        .filter(attribute => !isInheritedAttribute(attribute, isSubType, isContainerType));
      return (
        <IssueTypesAdminAttributesTableCell
          isSubType={isSubType}
          issueTypeAttributes={issueTypeAttributes}
          issueAttributeDefinitions={this.props.issueAttributeDefinitions}
          container={container}
        />
      );
    }

    return null;
  }

  render() {
    return (
      <MatrixTable
        attachTestId
        className="IssueTypesAdminTable"
        data={this.state.tableData}
        expandColumnKey="title"
        actionColumnProps={this.actionColumn}
        actionOptions={this.getActionOptions}
        onRowAction={this.handleRowAction}
        actionMode={MatrixTable.ActionMode.COLUMN}
        onRowClick={this.onRowClick}
        maxHeight={Number.MAX_VALUE}
        rowClassName={this.setRowClassName}
        headerClassName="IssueTypesAdminTable__header"
      >
        {this.columns}
      </MatrixTable>
    );
  }
}

IssueTypesAdminTable.displayName = 'IssueAttributesAdminTable';

IssueTypesAdminTable.propTypes = {
  data: PropTypes.object.isRequired,
  issueTypeIds: PropTypes.arrayOf(PropTypes.string).isRequired,
  scrollDown: PropTypes.func.isRequired,
  onStatusChange: PropTypes.func.isRequired,
  openEditModal: PropTypes.func.isRequired,
  issueAttributeDefinitions: PropTypes.object,
  issueAttributeMappings: PropTypes.object,
  highlightLastRow: PropTypes.bool
};

export default IssueTypesAdminTable;
